import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';
import {
  auth,
  signIn as firebaseSignIn,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  type User,
} from '@/lib/firebase';

/**
 * 認証状態
 */
interface AuthState {
  /** 現在の認証ユーザー（未認証の場合は null） */
  user: User | null;
  /** 認証状態の初期化中かどうか */
  isLoading: boolean;
  /** エラーメッセージ */
  error: string | null;
}

/**
 * AuthContext 値
 */
export interface AuthContextValue extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
}

/**
 * Firebase Auth のエラーコードを日本語メッセージに変換
 */
function getFirebaseAuthErrorMessage(err: unknown): string {
  if (typeof err === 'object' && err !== null && 'code' in err) {
    const code = (err as { code: string }).code;
    switch (code) {
      case 'auth/invalid-email':
        return 'メールアドレスの形式が正しくありません。';
      case 'auth/user-disabled':
        return 'このアカウントは無効になっています。管理者にお問い合わせください。';
      case 'auth/user-not-found':
      case 'auth/wrong-password':
      case 'auth/invalid-credential':
        return 'メールアドレスまたはパスワードが正しくありません。';
      case 'auth/too-many-requests':
        return 'ログイン試行回数が上限に達しました。しばらくしてから再度お試しください。';
      case 'auth/network-request-failed':
        return 'ネットワークエラーが発生しました。接続を確認してください。';
      default:
        return 'ログインに失敗しました。しばらくしてから再度お試しください。';
    }
  }
  return 'ログインに失敗しました。メールアドレスとパスワードを確認してください。';
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * 認証状態プロバイダー
 *
 * アプリ全体で単一の onAuthStateChanged リスナーを共有する。
 * TanStack Router の beforeLoad ガードでこの context を参照するため、
 * main.tsx 側で isLoading === false になってから RouterProvider を描画する。
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    isLoading: true,
    error: null,
  });

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setState({ user, isLoading: false, error: null });
    });
    return () => unsubscribe();
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    setState((prev) => ({ ...prev, error: null }));
    try {
      await firebaseSignIn(email, password);
    } catch (err) {
      const message = getFirebaseAuthErrorMessage(err);
      setState((prev) => ({ ...prev, error: message }));
    }
  }, []);

  const logout = useCallback(async () => {
    setState((prev) => ({ ...prev, error: null }));
    try {
      await firebaseSignOut();
    } catch {
      setState((prev) => ({
        ...prev,
        error: 'ログアウトに失敗しました。再度お試しください。',
      }));
    }
  }, []);

  const value: AuthContextValue = {
    ...state,
    login,
    logout,
    isAuthenticated: state.user !== null,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * 認証状態を取得する Hook
 */
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth は AuthProvider 内で使用してください');
  }
  return ctx;
}
